import React from 'react';
import { motion } from 'framer-motion';
import { Wrench } from 'lucide-react';

// Platform metadata — order controls the layout on the maintenance page.
const SOCIAL_META = [
  { key: 'spotify_url',    label: 'Spotify',    icon: 'fa-brands fa-spotify',   color: '#1DB954' },
  { key: 'soundcloud_url', label: 'SoundCloud', icon: 'fa-brands fa-soundcloud',color: '#FF5500' },
  { key: 'instagram_url',  label: 'Instagram',  icon: 'fa-brands fa-instagram', color: '#E1306C' },
  { key: 'tiktok_url',     label: 'TikTok',     icon: 'fa-brands fa-tiktok',    color: '#25F4EE' },
  { key: 'facebook_url',   label: 'Facebook',   icon: 'fa-brands fa-facebook',  color: '#1877F2' },
  { key: 'messenger_url',  label: 'Messenger',  icon: 'fa-brands fa-facebook-messenger', color: '#00B2FF' },
];

const MaintenancePage = ({ logoUrl, message, socials = {} }) => {
  const visible = SOCIAL_META
    .map((s) => ({ ...s, url: (socials[s.key] || '').trim() }))
    .filter((s) => !!s.url);

  return (
    <div
      className="min-h-screen bg-[#09090B] text-white flex flex-col items-center justify-center px-6 relative overflow-hidden"
      data-testid="maintenance-page"
    >
      {/* Background grain */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9' numOctaves='3' /%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#00F0FF]/5 via-transparent to-[#7c3aed]/5" />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 max-w-lg text-center"
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Kehele Keff Music"
            className="mx-auto mb-8 h-16 sm:h-20 object-contain"
            data-testid="maintenance-logo"
          />
        ) : (
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 mb-8">
            <Wrench className="text-[#00F0FF]" size={28} />
          </div>
        )}

        <div className="inline-block px-3 py-1 mb-4 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/10 text-[#00F0FF] text-[10px] uppercase tracking-[0.25em]">
          Under Maintenance
        </div>

        <h1
          className="text-3xl sm:text-4xl md:text-5xl font-heading font-medium tracking-tight mb-4"
          data-testid="maintenance-title"
        >
          We'll be right back
        </h1>

        <p className="text-[#A1A1AA] text-sm sm:text-base leading-relaxed mb-8" data-testid="maintenance-message">
          {message ||
            'The store is temporarily closed for updates. New releases and improvements are on the way — thanks for your patience.'}
        </p>

        {visible.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3 text-xs" data-testid="maintenance-socials">
            {visible.map((s) => (
              <a
                key={s.key}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#27272A] bg-[#0F0F11] text-[#D4D4D8] hover:border-[#00F0FF]/40 hover:text-white transition-colors"
                data-testid={`maintenance-social-${s.label.toLowerCase()}`}
              >
                <i className={s.icon} style={{ color: s.color }} /> {s.label}
              </a>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default MaintenancePage;
